/**
 * Notification kinds, channels and settings — the registry M5 is built on (spec
 * `specs/M5-notifications.md` §2).
 *
 * The vocabulary is monday's, verbatim, because `/v2 notifications_settings` has to answer
 * monday's kind names and a later import has to line up with them: the *setting* kind
 * (`automation_system`) and the *notification* kind name written on a row
 * (`automation_system_notify`) genuinely differ for some rows, and both are recorded here
 * `[api:discovery/api-corpus/notifications/README.md §settings matrix]`.
 *
 * A kind we do not raise is **not** deleted: it carries `supported: false`, so the settings API
 * still answers monday's full list of 31 and a later module can flip one on without a migration.
 */
import { z } from 'zod';
/** monday's `ChannelType`. Slack exists in the enum and is answered as "no such channel" (M10). */
export const NOTIFICATION_CHANNELS = ['Monday', 'Email', 'Slack'];
export const notificationChannelSchema = z.enum(NOTIFICATION_CHANNELS);
/** monday's `ChannelEditableStatus`. */
export const CHANNEL_EDITABLE_STATUSES = [
    'AllRelatedNotificationsDontHaveChannel',
    'AlwaysEnabled',
    'Editable',
];
/** monday's `ScopeType`. */
export const NOTIFICATION_SCOPE_TYPES = ['User', 'AccountNewUserDefaults'];
/** monday's `BoardMuteState`. `CUSTOM_SETTINGS` is only reachable through `enabled`. */
export const BOARD_MUTE_STATES = [
    'NOT_MUTED',
    'MUTE_ALL',
    'CURRENT_USER_MUTE_ALL',
    'MENTIONS_AND_ASSIGNS_ONLY',
    'CUSTOM_SETTINGS',
];
export const boardMuteStateSchema = z.enum(BOARD_MUTE_STATES);
/** monday's `CustomizableBoardSettings`. */
export const CUSTOMIZABLE_BOARD_SETTINGS = ['IM_MENTIONED', 'IM_ASSIGNED', 'AUTOMATION_NOTIFY'];
export const customizableBoardSettingSchema = z.enum(CUSTOMIZABLE_BOARD_SETTINGS);
/** The section a kind sits in on Profile ▸ Notifications, in monday's order `[ui §2]`. */
export const NOTIFICATION_SECTIONS = [
    'Communication',
    'Collaboration',
    'Requests',
    'Automations',
    'Sign-ups',
    'Security',
    'Admin',
    'Agents',
];
const editable = (enabled) => ({ enabled, editable: 'Editable' });
const forced = () => ({ enabled: true, editable: 'AlwaysEnabled' });
const absent = () => ({
    enabled: false,
    editable: 'AllRelatedNotificationsDontHaveChannel',
});
const noSlack = absent;
function def(kind, kindName, legacyKind, section, description, priority, channels, flags = {}) {
    return {
        kind,
        kindName,
        legacyKind,
        section,
        description,
        isForAdminsOnly: flags.admin === true,
        isForNonGuestsOnly: flags.nonGuest === true,
        supported: flags.supported !== false,
        priority,
        channels: {
            Monday: channels.Monday ?? editable(true),
            Email: channels.Email ?? editable(false),
            Slack: channels.Slack ?? noSlack(),
        },
    };
}
/**
 * The 31 kinds monday reports for this account. The first 14 are ours; the rest describe
 * monday's own commercial surface and are `supported: false`.
 */
export const NOTIFICATION_KINDS = [
    /* ── supported ─────────────────────────────────────────────────────────── */
    def('mention', 'mention', 5, 'Communication', 'Mentioned me in an update or reply', 10, { Monday: forced(), Slack: editable(true) }),
    def('assign', 'board_assigned_in_column', 28, 'Collaboration', 'Assigned me to an item', 20, { Slack: editable(true) }),
    def('reply_your_post', 'reply_your_post', 8, 'Communication', 'Replied to an update I wrote', 30, {}),
    def('reply_your_reply', 'reply_your_reply', 9, 'Communication', 'Replied to a thread I commented on or reacted to', 40, {}),
    def('new_post_on_owned_item', 'new_post_im_owner', 6, 'Communication', 'Wrote an update on an item I own', 50, {}),
    def('new_post_on_subscribed_item', 'new_post_im_member', 7, 'Communication', "Wrote an update on an item I’m subscribed to", 60, { Monday: editable(false) }),
    def('like_your_post', 'like_your_post', 11, 'Communication', 'Reactions to my update', 70, { Email: absent() }),
    def('invitation', 'invitation', 12, 'Collaboration', 'Invitations to workspace, board, doc, item, or team', 80, {}),
    def('automation_product', 'automation_notify', 30, 'Automations', 'Automations with a "notify" step this does not include "send an email" automations', 25, {}),
    def('automation', 'automation', 31, 'Automations', 'Automations via an automation', 26, {}),
    def('automation_system', 'automation_system_notify', 66, 'Automations', "Automation failures when automations don’t run as expected", 27, { Monday: forced(), Email: absent() }),
    def('api', null, 0, 'Automations', 'Platform API custom notifications using the GraphQL API', 90, {}),
    def('ask_for_permissions', 'ask_for_permissions', 15, 'Requests', 'Requests access to boards & dashboards', 85, {}),
    def('daily_digest', null, 40, 'Communication', 'My highlights', 100, { Monday: absent() }, { nonGuest: true }),
    /* ── monday-only, answered by the settings API and never raised ─────────── */
    def('campaigns_notifications', 'campaigns_notifications', 41, 'Communication', 'Campaigns notifications from Monday Campaigns', 900, {}, { supported: false }),
    def('template_update', 'template_update', 42, 'Collaboration', 'Template changes by the template owner', 900, { Email: absent() }, { supported: false }),
    def('request_to_install_and_purchase_apps', 'request_to_install_and_purchase_apps', 43, 'Requests', 'Requests installation to install & purchase apps', 900, {}, { supported: false }),
    def('notify_invitee_join', 'notify_invitee_join', 44, 'Sign-ups', 'Signed up after I have invited them', 900, {}, { supported: false }),
    def('notify_invitee_didnt_join', 'notify_invitee_didnt_join', 45, 'Sign-ups', 'Didn’t sign up after I have invited them', 900, {}, { supported: false }),
    def('notify_auth_domain_signup', 'notify_auth_domain_signup', 46, 'Sign-ups', 'Signed up with an email address from my account domain', 900, {}, { supported: false, admin: true }),
    def('dlp_scans_digest_setting', 'dlp_scans_digest', 47, 'Security', 'Violation summaries for breaching DLP policies', 900, {}, { supported: false }),
    def('dlp_file_deleted_setting', 'dlp_file_deleted', 48, 'Security', 'File has been deleted for breaching data policies', 900, { Email: absent() }, { supported: false }),
    def('dlp_update_deleted_setting', 'dlp_update_deleted', 49, 'Security', 'Update has been deleted or redacted for breaching data policies', 900, { Email: absent() }, { supported: false }),
    def('agent_resources', 'agent_resources', 50, 'Agents', 'Agent gains or loses access to boards, docs and other content you own', 900, { Email: absent() }, { supported: false }),
    def('plan_enforcer_notifications_setting', 'plan_enforcer_notifications', 51, 'Admin', 'Automatic plan upgrade when an automatic plan upgrade is scheduled to occur', 900, { Monday: forced(), Email: forced() }, { supported: false, admin: true }),
    def('invitation_requests_admins_reminder_setting', 'invitation_requests_admins_reminder', 52, 'Admin', 'Pending invite requests when invite requests have been pending for over 7 days', 900, {}, { supported: false, admin: true }),
    def('feature_enforcement_ai_credits_on_warning_period_setting', 'feature_enforcement_ai_credits_on_warning_period', 53, 'Admin', 'AI Usage Warning notify when ai usage exceeds 80%', 900, {}, { supported: false, admin: true }),
    def('feature_enforcement_ai_credits_on_grace_period_setting', 'feature_enforcement_ai_credits_on_grace_period', 54, 'Admin', 'AI Credit Limit (Active) notify when the limit is reached but AI features keep running', 900, {}, { supported: false, admin: true }),
    def('feature_enforcement_ai_credits_on_blocked_period_setting', 'feature_enforcement_ai_credits_on_blocked_period', 55, 'Admin', 'AI Credit Limit (Stopped) notify when 100% usage is reached and AI features stop', 900, {}, { supported: false, admin: true }),
    def('feature_enforcement_ai_credits_member_request_on_grace_period_setting', 'feature_enforcement_ai_credits_member_request_on_grace_period', 56, 'Admin', 'AI Credit Request (Active) notify when a user requests credits while AI features are still running', 900, {}, { supported: false, admin: true }),
    def('feature_enforcement_ai_credits_member_request_on_blocked_period_setting', 'feature_enforcement_ai_credits_member_request_on_blocked_period', 57, 'Admin', 'AI Credit Request (Stopped) notify when a user requests credits after usage has stopped', 900, {}, { supported: false, admin: true }),
];
/**
 * **Ours, not monday's** (M10 Tasks, round T1).
 *
 * `NOTIFICATION_KINDS` above is monday's list of 31 verbatim and is what `/v2
 * notifications_settings` and the Profile ▸ Notifications matrix answer — so it stays at 31 and
 * these are kept apart from it. They are still real kinds: `notificationKind()` resolves them,
 * `notify()` raises them, and the per-user switches live in the Tasks preferences
 * (`tasks.remindOverdue`, `tasks.notifyOnComplete`, `tasks.digestFrequency`) rather than in
 * monday's matrix, which has no row for them. Open question q558.
 *
 * **There is no per-task overdue kind** (P-269, round T6). One row per overdue task is what
 * produced 550 bell notifications in a minute on the first run after the Tasks release, so the
 * reminder is now `task_overdue_digest`: **one row per user per day**, carrying the count and
 * the first five names. Nothing else is allowed to raise an overdue notification.
 *
 * The nudge deliberately has **no** kind: it posts an update mentioning the assignee, which
 * raises monday's own `mention` through the updates service (`specs/M10-tasks.md` §5).
 */
export const TASK_NOTIFICATION_KINDS = [
    def('task_completed', null, 70, 'Collaboration', 'Completed a task I assigned to them', 21, {}),
    def('task_overdue_digest', null, 71, 'Collaboration', 'A daily summary of my overdue tasks', 22, {}),
    def('task_digest', null, 72, 'Collaboration', 'My tasks today', 23, { Monday: editable(false) }),
];
const BY_KIND = new Map([...NOTIFICATION_KINDS, ...TASK_NOTIFICATION_KINDS].map((k) => [k.kind, k]));
const BY_KIND_NAME = new Map(NOTIFICATION_KINDS.filter((k) => k.kindName !== null).map((k) => [k.kindName, k]));
export function notificationKind(kind) {
    return BY_KIND.get(kind);
}
export function notificationKindByName(kindName) {
    return BY_KIND_NAME.get(kindName);
}
/** The kinds we actually raise, in fan-out priority order. */
export const SUPPORTED_NOTIFICATION_KINDS = NOTIFICATION_KINDS
    .filter((k) => k.supported)
    .slice()
    .sort((a, b) => a.priority - b.priority);
export const SUPPORTED_KIND_NAMES = SUPPORTED_NOTIFICATION_KINDS.map((k) => k.kind);
export const notificationKindSchema = z
    .string()
    .refine((value) => BY_KIND.has(value), { message: 'unknown notification kind' });
/**
 * Is the channel writable at all? A forced channel refuses a write (monday renders the checkbox
 * disabled) and a channel that does not exist for the kind refuses one too.
 */
export function isChannelEditable(kind, channel) {
    return notificationKind(kind)?.channels[channel].editable === 'Editable';
}
/** Registry default for a (kind, channel), before per-user and per-account overrides. */
export function channelDefault(kind, channel) {
    return notificationKind(kind)?.channels[channel].enabled ?? false;
}
/* ── board mute (spec §6.3, rule R5) ───────────────────────────────────────── */
/** Does `state` still let `kindName` through for this board? */
export function muteAllows(state, enabled, kind) {
    switch (state) {
        case 'NOT_MUTED':
            return true;
        case 'MUTE_ALL':
        case 'CURRENT_USER_MUTE_ALL':
            return false;
        case 'MENTIONS_AND_ASSIGNS_ONLY':
            return kind === 'mention' || kind === 'assign';
        case 'CUSTOM_SETTINGS': {
            const wanted = kind === 'mention' ? 'IM_MENTIONED'
                : kind === 'assign' ? 'IM_ASSIGNED'
                    : kind === 'automation_product' || kind === 'automation' ? 'AUTOMATION_NOTIFY'
                        : null;
            return wanted !== null && enabled.includes(wanted);
        }
    }
}
export const NOTIFICATION_TEXT_LIMIT = 200;
/**
 * The two denormalised strings a row carries. Rendered at **write** time, like monday's, so a
 * notification stays readable after the item is renamed or deleted.
 */
export function renderNotification(input) {
    const actor = input.actorName || 'Someone';
    const item = input.itemName ? `"${input.itemName}"` : 'an item';
    const body = input.bodyText ? input.bodyText.slice(0, NOTIFICATION_TEXT_LIMIT) : null;
    switch (input.kind) {
        case 'mention':
            return { title: `${actor} mentioned you in an update`, text: body };
        case 'assign':
            return { title: `${actor} assigned you to the item ${item}`, text: null };
        case 'new_post_on_owned_item':
            return { title: `${actor} wrote an update on an item you own`, text: body };
        case 'new_post_on_subscribed_item':
            return { title: `${actor} wrote an update on an item you're subscribed to`, text: body };
        case 'reply_your_post':
            return { title: `${actor} replied to an update you wrote`, text: body };
        case 'reply_your_reply':
            return { title: `${actor} replied to a thread you commented on`, text: body };
        case 'like_your_post':
            return { title: `${actor} reacted to your update`, text: null };
        case 'invitation':
            return {
                title: input.boardName ? `${actor} added you to the board "${input.boardName}"` : `${actor} invited you`,
                text: null,
            };
        case 'automation_system':
            return { title: 'Your automation was disabled', text: input.message ?? null };
        case 'automation_product':
        case 'automation':
            return { title: actor, text: input.message ?? null };
        /* ── ours (M10 Tasks) ────────────────────────────────────────────────── */
        case 'task_completed':
            return { title: `${actor} completed ${item}, which you assigned`, text: null };
        case 'task_overdue_digest': {
            /*
             * P-269: the job owns both halves of this row's wording — it is the only kind whose text
             * is a list of *other* items — so it passes them as one `message`, headline first:
             * "You have 7 overdue tasks\nAlpha, Beta, Gamma and 4 more".
             */
            const [headline = '', ...rest] = (input.message ?? '').split('\n');
            return {
                title: headline.length > 0 ? headline : 'You have overdue tasks',
                text: rest.length > 0 ? rest.join('\n') : null,
            };
        }
        case 'task_digest':
            return { title: 'Your tasks today', text: input.message ?? null };
        case 'api':
            // Measured: monday puts the *caller's display name* in `title` and the supplied string in
            // `text` `[api §M5-a2]`.
            return { title: actor, text: input.message ?? null };
        default:
            return { title: actor, text: input.message ?? body };
    }
}
/**
 * `<kind>:<boardId>:<itemId>:<updateId>` for the kinds that coalesce while unread, `null` for the
 * ones that must never coalesce (a script that sends two API notifications really does send two).
 */
export function notificationDedupeKey(input) {
    /*
     * `task_overdue_digest` never coalesces either (P-269): the "one per user per day" rule is
     * enforced by the job's own `tasks.remindersLastDigest` guard, and a `dedupe_key` here would
     * additionally swallow *today's* digest whenever yesterday's is still unread.
     */
    const NEVER = new Set([
        'api',
        'automation',
        'automation_product',
        'automation_system',
        'invitation',
        'task_overdue_digest',
    ]);
    if (NEVER.has(input.kind))
        return null;
    return [input.kind, input.boardId ?? '', input.itemId ?? '', input.updateId ?? ''].join(':').slice(0, 160);
}
/* ── delivery constants (spec §7, §3) ──────────────────────────────────────── */
/** How long a pending email waits so a burst on one thread arrives as one mail. */
export const EMAIL_GRACE_MS = 60_000;
/** At or above this many pending rows for one recipient, one rollup mail replaces them. */
export const EMAIL_ROLLUP_THRESHOLD = 3;
/** The digest fires at this local hour. */
export const DIGEST_HOUR = 8;
/** A user gets at most one digest per this window, whatever the scheduler does. */
export const DIGEST_MIN_INTERVAL_MS = 20 * 60 * 60_000;
/** Rows in one digest mail. */
export const DIGEST_LIMIT = 50;
/** `notifications` older than this are deleted (ASSUMED, q284). */
export const NOTIFICATION_RETENTION_DAYS = 180;
/** A soft-deleted row is purged after this. */
export const NOTIFICATION_TRASH_DAYS = 7;
/** The bell's page size. */
export const NOTIFICATION_PAGE_SIZE = 25;
/** The bell's three tabs `[ui §1]`. */
export const NOTIFICATION_TABS = ['all', 'mentioned', 'assigned'];
/** Which kinds a tab shows. `all` is everything. */
export function kindsForTab(tab) {
    if (tab === 'mentioned')
        return ['mention'];
    if (tab === 'assigned')
        return ['assign'];
    return null;
}
/** The Update feed's five tabs `[ui §3]`. */
export const INBOX_TABS = ['all_updates', 'mentioned', 'bookmarked', 'all_account', 'scheduled'];
/**
 * The tabs that have a store behind them; the rest render a "not available yet" body
 * (`specs/M5-notifications.md` §8). **`bookmarked` joined them in round 5m**: B7 measured
 * `Bookmark to update feed` in the update "…" menu, round 5 deferred it as q256, and
 * migration 0014's `update_bookmarks` is that store (`specs/M7-C3-inbox-my-work.md` §4).
 * `all_account` and `scheduled` are still monday-only surfaces.
 */
export const INBOX_TABS_IMPLEMENTED = ['all_updates', 'mentioned', 'bookmarked'];
export const INBOX_PAGE_SIZE = 20;
//# sourceMappingURL=index.js.map